import { COMPLETE_PREPARATION_TARGET_STATUS } from '../../core/models/weight-surcharge.constants';
import { toJsDate } from '../../core/utils/firestore-date';

/**
 * #2164 lot 11 — « une étape terminée ne se refait pas par mégarde ».
 *
 * Constat du 28/10 (BF-VM01-000003-6) : préparé à 13:29, préparation refaite à
 * 13:52. La page de préparation ne regardait jamais le statut du colis ;
 * « Terminer » réécrivait l'étiquette et la photo, puis le serveur répondait
 * « succès » sans rien faire (colis déjà au statut cible).
 *
 * Une étape faite s'affiche « fait, par qui, quand ». Le « qui » et le
 * « quand » se lisent dans la ligne de `statusHistory` que le SERVEUR écrit à
 * la transition (`transitionShipmentStatus` : `operator`, `timestamp`) ; aucune
 * étape ne porte de champ « fait par ».
 *
 * Fonctions PURES.
 */

type Doc = Record<string, any> | null | undefined;

export interface StepDone {
  at: Date | null;
  by: string | null;
}

/**
 * Statuts où la préparation est À FAIRE (ou à refaire après un incident levé :
 * la résolution repasse le colis en `received_at_warehouse`). Même liste que
 * l'action « Préparer » de la fiche colis, plus l'incident en cours, qui a son
 * propre parcours sur cet écran.
 */
const PREPARATION_OPEN_STATUSES: ReadonlySet<string> = new Set([
  'received_at_warehouse',
  'warehouse_processing',
  'preparation_blocked',
]);

function currentStatus(shipment: Doc): string | undefined {
  const status = shipment?.['status'];
  if (typeof status === 'string') return status;
  return typeof status?.['current'] === 'string' ? status['current'] : undefined;
}

/** Dernière ligne de `statusHistory` pour ce statut, ou `undefined`. */
function lastHistoryEntry(shipment: Doc, status: string): Record<string, any> | undefined {
  const history = shipment?.['statusHistory'];
  if (!Array.isArray(history)) return undefined;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i]?.['status'] === status) return history[i];
  }
  return undefined;
}

/**
 * Préparation terminée : le colis a franchi `warehouse_sorted` (tampon serveur
 * `preparation.sortedAt`, ou la ligne d'historique pour un colis antérieur au
 * tampon) et n'est pas revenu dans un statut où elle est à faire.
 */
export function preparationDone(shipment: Doc): StepDone | null {
  const status = currentStatus(shipment);
  if (!status || PREPARATION_OPEN_STATUSES.has(status)) return null;
  const entry = lastHistoryEntry(shipment, COMPLETE_PREPARATION_TARGET_STATUS);
  const sortedAt = shipment?.['preparation']?.['sortedAt'];
  if (!entry && !sortedAt) return null;
  return {
    at: toJsDate(entry?.['timestamp'] ?? sortedAt),
    by: operatorName(entry),
  };
}

/** Première ligne de `statusHistory` pour ce statut, ou `undefined`. */
function firstHistoryEntry(shipment: Doc, status: string): Record<string, any> | undefined {
  const history = shipment?.['statusHistory'];
  if (!Array.isArray(history)) return undefined;
  return history.find(e => e?.['status'] === status);
}

function operatorName(entry: Record<string, any> | undefined): string | null {
  const name = entry?.['operator']?.['name'];
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/**
 * Réception faite : par qui, quand. La PREMIÈRE ligne `received_at_warehouse`
 * est la réception ; une levée d'incident repasse aussi par ce statut, plus tard.
 * `null` si l'historique ne la porte pas et qu'aucun tampon d'arrivée n'existe.
 */
export function receptionDone(shipment: Doc): StepDone | null {
  const entry = firstHistoryEntry(shipment, 'received_at_warehouse');
  const stamp = shipment?.['befretIntegration']?.['warehouseArrival']
    ?? shipment?.['warehouse']?.['receivedAt']
    ?? shipment?.['timestamps']?.['warehouseAt'];
  const at = toJsDate(entry?.['timestamp'] ?? stamp);
  if (!entry && !at) return null;
  return { at, by: operatorName(entry) };
}

export interface WeighingDone extends StepDone {
  kg: number;
}

/**
 * Pesée Befret faite : par qui, quand, combien. La DERNIÈRE ligne
 * `weighing_completed` (écrite par `recordBefretWeight`) fait foi ; à défaut,
 * `warehouse.weighedAt` et `warehouse.befretWeight`.
 */
export function weighingDone(shipment: Doc): WeighingDone | null {
  const recorded = Number(shipment?.['warehouse']?.['befretWeight']);
  if (!Number.isFinite(recorded) || recorded <= 0) return null;
  const entry = lastHistoryEntry(shipment, 'weighing_completed');
  return {
    at: toJsDate(entry?.['timestamp'] ?? shipment?.['warehouse']?.['weighedAt']),
    by: operatorName(entry),
    kg: recorded,
  };
}
